/**
 * ClaimCallStep
 *
 * Displays the Voice AI call step of the claims filing flow. Renders the
 * in-progress/failed/success states, a summarized call outcome (when known),
 * and fallback manual call options derived from the linked coverage. Also
 * provides a button to retry the call and a modal to place a manual call.
 *
 * Props
 * - linkedCoverage: Optional coverage context used to surface phone numbers
 * - callStatus: UI state for the call ('idle' | 'in_progress' | ...)
 * - isCallOutcomeReady: When true, outcome controls/summary can render
 * - onRetryCall: Triggers a new Voice AI call
 * - onBackToReview: Navigates back to the Review step
 */
import React from 'react';

import { CoverageSchema } from '@/services/types/coverage-types';

import { CallStatus } from '../hooks/useClaimFilingState';
import { ClaimCall } from './claim-call';

/** Props for `ClaimCallStep`. */
interface ClaimCallStepProps {
  /** Optional coverage context used to derive carrier contact options. */
  linkedCoverage?: CoverageSchema;
  /** Matter id for updates feed lookup; avoids unsafe coverage casts. */
  matterId?: string;
  /** Current call UI status. */
  callStatus: CallStatus;
  /** When true, outcome summary/controls can be shown. */
  isCallOutcomeReady: boolean;
  /** Handler to start/retry a Voice AI call. */
  onRetryCall: () => void;
  /** Handler to send the task directly to BPO (max_calls_override=0). */
  onSendToBpo?: () => void;
  /** Navigate back to the Review step. */
  onBackToReview: () => void;
  /** Whether the start-call mutation is in-flight. */
  isStartingCall?: boolean;
  /** Optional error message from start-call mutation (for inline display). */
  startCallError?: string | null;
}

export const ClaimCallStep: React.FC<ClaimCallStepProps> = ({
  linkedCoverage,
  matterId,
  callStatus,
  isCallOutcomeReady,
  onRetryCall,
  onSendToBpo,
  onBackToReview,
  isStartingCall,
  startCallError,
}) => {
  return (
    <ClaimCall
      linkedCoverage={linkedCoverage}
      matterId={matterId}
      callStatus={callStatus}
      isCallOutcomeReady={isCallOutcomeReady}
      onRetryCall={onRetryCall}
      onSendToBpo={onSendToBpo}
      onBackToReview={onBackToReview}
      isStartingCall={isStartingCall}
      startCallError={startCallError}
    />
  );
};
